// O ecrã de jogo — quando a app se cala para deixar jogar (IDEA-034 v3).
//
// Um tabuleiro a ser resolvido não é uma página da app: é a única coisa que
// está a acontecer. Enquanto se joga, as duas navegações desaparecem — a barra
// de cima no desktop e o carrossel de baixo no telemóvel — e fica o tabuleiro,
// o relógio, e uma saída no canto superior esquerdo.
//
// Porquê tirar a navegação e não deixá-la lá:
//
//   O carrossel de baixo vive exactamente onde o polegar está a jogar. Num
//   Shikaku arrasta-se; num sudoku carrega-se num teclado que está a dois
//   centímetros dele. Mais cedo ou mais tarde um gesto do jogo acaba a mudar de
//   página, e com o relógio a andar isso não é um engano pequeno.
//
//   E a saída deixa de ser inofensiva: sair a meio de uma ronda que a outra
//   pessoa já jogou é desistir. Uma coisa dessas não pode estar a um toque
//   acidental de distância — tem de ser um botão que se procura.
//
// O sinal vive num contexto e não na rota, porque não é a página que é imersiva
// (a página do sudoku, parada, é uma página normal com regras e histórico) — é
// o estado de estar a jogar.

import { createContext, useContext, useEffect, useMemo, useState } from "react";

interface EcraDeJogoCtx {
  /** True enquanto um tabuleiro está a ser jogado. */
  aJogar: boolean;
  setAJogar: (v: boolean) => void;
}

const Contexto = createContext<EcraDeJogoCtx | null>(null);

export function EcraDeJogoProvider({ children }: { children: React.ReactNode }) {
  const [aJogar, setAJogar] = useState(false);
  const valor = useMemo(() => ({ aJogar, setAJogar }), [aJogar]);
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

/**
 * Lê o estado. Fora do provider devolve `false` — assim qualquer componente
 * pode assumir "não se está a jogar" como resposta segura.
 */
export function useEcraDeJogo(): EcraDeJogoCtx {
  return useContext(Contexto) ?? { aJogar: false, setAJogar: () => {} };
}

/**
 * Declara que este ecrã está a ser jogado, enquanto o estiver.
 *
 * A limpeza no fim é o que importa: sem ela, sair da página a meio de um jogo
 * deixava a app sem navegação nenhuma e sem maneira de a trazer de volta.
 */
export function useModoDeJogo(aJogar: boolean): void {
  const { setAJogar } = useEcraDeJogo();

  useEffect(() => {
    setAJogar(aJogar);
    return () => setAJogar(false);
  }, [aJogar, setAJogar]);
}
